import React from "react";
import { TileType } from "../../types";

interface PipeSvgProps {
  type: TileType;
  live: boolean;
  isSource: boolean;
  isEnd: boolean;
}

const PIPE_COLOR = "#D97706"; // amber — idle path tile
const LIVE_COLOR = "#4ade80"; // green — live / source
const SOURCE_COLOR = "#4ade80"; // green — source matches live power
const END_COLOR = "#ef4444"; // red — end target (turns green on connect)

const SW = 13; // pipe stroke width (chunky)
const SIZE = 80; // SVG viewBox dimension
const MID = SIZE / 2; // 40
// Pipe endpoints extend BLEED px past the viewBox boundary.
// The SVG is overflow:visible so they render; the parent motion.div has
// overflow:hidden (CSS) which clips them flush at the tile edge.
// This eliminates the sub-pixel CSS-grid seam that would otherwise show
// as a hairline gap between adjacent tiles.
const BLEED = 3;

// Lollipop radii
const DISC_R = 10; // regular deadend cap
const DISC_R_END = 13; // end tile cap
// Disc sits this many px inside the tile edge so it isn't flush/cut-off
const DISC_INSET = 5;

export const PipeSvg = React.memo(function PipeSvg({
  type,
  live,
  isSource,
  isEnd,
}: PipeSvgProps) {
  const color = isSource
    ? SOURCE_COLOR
    : live
      ? LIVE_COLOR
      : isEnd
        ? END_COLOR
        : PIPE_COLOR;

  const pipeStrokeStyle: React.CSSProperties = {
    stroke: color,
    transition: "stroke 300ms ease",
  };

  const commonStroke = {
    strokeWidth: SW,
    strokeLinecap: "square" as const, // flat cap = two adjacent tiles meet flush, no blob at junction
    strokeLinejoin: "round" as const,
    fill: "none",
  };

  const renderShape = () => {
    switch (type) {
      case "empty":
        return null;

      case "deadend": {
        const discR = isEnd ? DISC_R_END : DISC_R;
        // Disc sits DISC_INSET px inside the tile edge — not flush, looks clean
        const discY = discR + DISC_INSET;
        const discFill = isSource
          ? SOURCE_COLOR
          : isEnd && !live
            ? END_COLOR
            : color;

        return (
          <>
            {/* Arm: center → BLEED px past top edge. Clipped by tile div overflow:hidden. */}
            <line
              x1={MID}
              y1={MID}
              x2={MID}
              y2={-BLEED}
              {...commonStroke}
              style={pipeStrokeStyle}
            />

            {/* Lollipop disc */}
            {isEnd ? (
              <>
                <circle
                  cx={MID}
                  cy={discY}
                  r={discR}
                  fill={live ? LIVE_COLOR : END_COLOR}
                  stroke="none"
                  style={{
                    opacity: live ? 1 : 0.65,
                    transition: "opacity 300ms ease",
                  }}
                />
                {/* Socket ring */}
                <circle
                  cx={MID}
                  cy={discY}
                  r={7}
                  fill={live ? "#052e16" : "#1f2937"}
                  stroke="none"
                />
                {/* Socket dot */}
                <circle
                  cx={MID}
                  cy={discY}
                  r={3}
                  fill={live ? LIVE_COLOR : END_COLOR}
                  stroke="none"
                />
              </>
            ) : (
              <circle
                cx={MID}
                cy={discY}
                r={discR}
                fill={discFill}
                stroke="none"
              />
            )}
          </>
        );
      }

      case "straight":
        // Bleed past both edges; clipped by tile div
        return (
          <line
            x1={MID}
            y1={-BLEED}
            x2={MID}
            y2={SIZE + BLEED}
            {...commonStroke}
            style={pipeStrokeStyle}
          />
        );

      case "lbend":
        // Smooth quarter-arc: top-center → right-center, both endpoints bleed past tile edge
        return (
          <path
            d={`M ${MID} ${-BLEED} Q ${MID} ${MID} ${SIZE + BLEED} ${MID}`}
            {...commonStroke}
            style={pipeStrokeStyle}
          />
        );

      case "tee":
        return (
          <>
            <line
              x1={MID}
              y1={-BLEED}
              x2={MID}
              y2={SIZE + BLEED}
              {...commonStroke}
              style={pipeStrokeStyle}
            />
            <line
              x1={MID}
              y1={MID}
              x2={SIZE + BLEED}
              y2={MID}
              {...commonStroke}
              style={pipeStrokeStyle}
            />
          </>
        );

      case "cross":
        return (
          <>
            <line
              x1={-BLEED}
              y1={MID}
              x2={SIZE + BLEED}
              y2={MID}
              {...commonStroke}
              style={pipeStrokeStyle}
            />
            <line
              x1={MID}
              y1={-BLEED}
              x2={MID}
              y2={SIZE + BLEED}
              {...commonStroke}
              style={pipeStrokeStyle}
            />
            <circle
              cx={MID}
              cy={MID}
              r={10}
              fill="none"
              style={pipeStrokeStyle}
              strokeWidth={4.5}
            />
            <circle cx={MID} cy={MID} r={5} fill="#1f2937" stroke="none" />
          </>
        );

      default:
        return null;
    }
  };

  return (
    // overflow:visible lets the BLEED region render outside the viewBox.
    // Actual clipping is done by the parent motion.div's CSS overflow:hidden.
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      width="100%"
      height="100%"
      style={{ display: "block" }}
      overflow="visible"
    >
      {renderShape()}

      {/* Source marker — green dashed ring */}
      {isSource && (
        <circle
          cx={MID}
          cy={MID}
          r={30}
          fill="rgba(74,222,128,0.08)"
          stroke={SOURCE_COLOR}
          strokeWidth={3.5}
          strokeDasharray="6 3"
        />
      )}

      {/* End marker — red ring → green when live */}
      {isEnd && (
        <circle
          cx={MID}
          cy={MID}
          r={30}
          fill={live ? "rgba(74,222,128,0.06)" : "rgba(239,68,68,0.06)"}
          strokeWidth={3.5}
          style={{
            stroke: live ? LIVE_COLOR : END_COLOR,
            transition: "stroke 300ms ease",
          }}
        />
      )}
    </svg>
  );
});
